import {vec2, vec3, mat3} from "_src/libs/gl-matrix";
import {EPSILON} from "_src/engine/constants";

const MISS          = 1;
const INTERSECTION  = 2;
const INSIDE        = 3;

const depthScale = vec3.fromValues(65536.0, 256.0, 1.0);
vec3.scale(depthScale, depthScale, 1.0 / 65793.0);

const intersectEvaluator = {

  MISS : MISS,
  INTERSECTION : INTERSECTION,
  INSIDE : INSIDE,

  triangleRayIntersection : (function() {

    const edge1 = vec3.create();
    const edge2 = vec3.create();

    const vec3Tmp1 = vec3.create();
    const vec3Tmp2 = vec3.create();

    return function (from, to, indices, offset, length, positions, result) {

      const point = result.point;

      const startInd = offset;
      const finalInd = startInd + length;

      let distance = Infinity;
      let intersected = false;

      for (let i = startInd; i < finalInd; i += 3) {

        const i1 = indices[i];
        const i2 = indices[i + 1];
        const i3 = indices[i + 2];

        const offset1 = i1 * 3;
        const offset2 = i2 * 3;
        const offset3 = i3 * 3;

        const x1 = positions[offset1];
        const x2 = positions[offset2];
        const x3 = positions[offset3];

        const y1 = positions[offset1 + 1];
        const y2 = positions[offset2 + 1];
        const y3 = positions[offset3 + 1];

        const z1 = positions[offset1 + 2];
        const z2 = positions[offset2 + 2];
        const z3 = positions[offset3 + 2];

        const e1 = vec3.set(edge1, x2 - x1, y2 - y1, z2 - z1);
        const e2 = vec3.set(edge2, x3 - x1, y3 - y1, z3 - z1);

        const normal = vec3.cross(vec3Tmp1, to, e2);
        const det = vec3.dot(e1, normal);

        // ray and tri are parallel
        if (det < EPSILON && det > -EPSILON) {
          continue;
        }

        const invDet = 1 / det;
        const tVec = vec3.set(vec3Tmp2, from[0] - x1, from[1] - y1, from[2] - z1);

        const u = vec3.dot(tVec, normal) * invDet;

        if (u < 0 || u > 1) {
          continue;
        }

        const qVec = vec3.cross(vec3Tmp1, tVec, e1);
        const v = vec3.dot(to, qVec) * invDet;

        if (v < 0 || u + v > 1) {
          continue;
        }

        const currDistance = vec3.dot(e2, qVec) * invDet;

        if (currDistance < distance) {

          intersected = true;

          distance = currDistance;

          result.primitive = i;

        }

      }

      if (intersected) {
        vec3.scaleAndAdd(point, from, to, distance);
      }

      result.distance = distance;

      return intersected;
    }

  })(),

  triangleLineIntersection : (function() {

    const edge1 = vec3.create();
    const edge2 = vec3.create();

    const vec3Tmp1 = vec3.create();
    const vec3Tmp2 = vec3.create();

    return function (from, to, indices, offset, length, positions, tlim, result) {

      const point = result.point;

      const startInd = offset;
      const finalInd = startInd + length;

      let distance = Infinity;
      let intersected = false;

      for (let i = startInd; i < finalInd; i += 3) {

        const i1 = indices[i];
        const i2 = indices[i + 1];
        const i3 = indices[i + 2];

        const offset1 = i1 * 3;
        const offset2 = i2 * 3;
        const offset3 = i3 * 3;

        const x1 = positions[offset1];
        const x2 = positions[offset2];
        const x3 = positions[offset3];

        const y1 = positions[offset1 + 1];
        const y2 = positions[offset2 + 1];
        const y3 = positions[offset3 + 1];

        const z1 = positions[offset1 + 2];
        const z2 = positions[offset2 + 2];
        const z3 = positions[offset3 + 2];

        const e1 = vec3.set(edge1, x2 - x1, y2 - y1, z2 - z1);
        const e2 = vec3.set(edge2, x3 - x1, y3 - y1, z3 - z1);

        const normal = vec3.cross(vec3Tmp1, to, e2);
        const det = vec3.dot(e1, normal);

        // ray and tri are parallel
        if (det < EPSILON && det > -EPSILON) {
          continue;
        }

        const invDet = 1 / det;
        const tVec = vec3.set(vec3Tmp2, from[0] - x1, from[1] - y1, from[2] - z1);

        const u = vec3.dot(tVec, normal) * invDet;

        if (u < 0 || u > 1) {
          continue;
        }

        const qVec = vec3.cross(vec3Tmp1, tVec, e1);
        const v = vec3.dot(to, qVec) * invDet;

        if (v < 0 || u + v > 1) {
          continue;
        }

        const currDistance = vec3.dot(e2, qVec) * invDet;
        const absDist = Math.abs(currDistance);

        if (absDist < distance && absDist <= tlim) {

          intersected = true;

          distance = absDist;

          vec3.scaleAndAdd(point, from, to, currDistance);
          result.primitive = i;
          result.distance = currDistance;

        }

      }

      return intersected;
    }

  })(),

  sphereRayIntersection : (function() {
    const vec3Tmp1 = vec3.create();
    const vec3Tmp2 = vec3.create();

    return function (from, to, indices, offset, length, positions, sizes, result) {

      const startInd = offset;
      const finalInd = startInd + length;

      let distance = Infinity;
      let intersected = false;

      for (let i = startInd; i < finalInd; i++) {
        const idx = indices ? indices[i] : i;
        const x = positions[idx * 3];
        const y = positions[idx * 3 + 1];
        const z = positions[idx * 3 + 2];

        const r = Math.abs(sizes[idx]);

        const sphereCenter = vec3.set(vec3Tmp1, x, y, z);
        const m = vec3.subtract(vec3Tmp2, from, sphereCenter);
        const b = vec3.dot(m, to);
        const c = vec3.dot(m, m) - r*r;

        const det = b * b - c;

        if (det < 0.0) {
          continue;
        }

        const t = -b + Math.sqrt(det);

        if (t < distance) {
          intersected = true;
          distance = t;
          result.primitive = i;
        }
      }

      if (intersected) {
        result.point = vec3.scaleAndAdd(result.point, from, to, distance);
      }

      result.distance = distance;

      return intersected;
    }
  })(),

  markVerticesInsideSphere : function (center, squaredRadius, indices, offset,
                                       length, positions, vertsMask, scale) {

    let numSelected = 0;
    const cx = center[0];
    const cy = center[1];
    const cz = center[2];

    const startInd = offset;
    const finalInd = startInd + length;

    for (let i = startInd; i < finalInd; ++i) {

      const ind = indices[i];

      if (vertsMask[ind]) {
        continue;
      }

      const offset = ind * 3;

      const dx = (positions[offset] - cx) * scale[0];
      const dy = (positions[offset + 1] - cy) * scale[1];
      const dz = (positions[offset + 2] - cz) * scale[2];

      if ((dx * dx + dy * dy + dz * dz) < squaredRadius) {

        vertsMask[ind] = 1;
        ++numSelected;

      }

    }

    return numSelected;

  },

  maskAABBTest : (function() {

    const vertex = vec3.create();
    const pixel = vec2.create();
    const min = vec2.create();
    const max = vec2.create();

    return function(maskPixels, width, height, bounding, minmax, mvp) {

      let selectedVerticesCounter = 0;
      vec2.set(min, Infinity, Infinity);
      vec2.set(max, -Infinity, -Infinity);

      for (let i = 0; i < 8; ++i) {

        const x = minmax[    3 * ((i & 0b100) >> 2)];
        const y = minmax[1 + 3 * ((i & 0b010) >> 1)];
        const z = minmax[2 + 3 * ((i & 0b001))];

        vec3.set(vertex, x, y, z);
        vec3.transformMat4(vertex, vertex, mvp);

        NDCtoScreen(vertex[0], vertex[1], width, height, pixel);

        vec2.min(min, min, pixel);
        vec2.max(max, max, pixel);

        const screenX = Math.round(pixel[0]);
        const screenY = Math.round(pixel[1]);

        if (inScreen(screenX, screenY, width, height)) {
          if (readValue(maskPixels, screenX, screenY, width)) {
            selectedVerticesCounter++;
          }
        }

      }

      if (0 < selectedVerticesCounter && selectedVerticesCounter < 8) {
        return INTERSECTION;
      }

      let x0 = Math.floor(clamp(0, width, min[0]));
      let y0 = Math.floor(clamp(0, height, min[1]));
      let x1 = Math.floor(clamp(0, width, max[0]));
      let y1 = Math.floor(clamp(0, height, max[1]));

      if (bounding[2] < x0 || bounding[0] > x1 ||
          bounding[3] < y0 || bounding[1] > y1) {
        return MISS;
      }

      let emptyPixels = 0;
      let intersected = false;
      let overlapped = false;

      if (bounding[0] <= x0 && x1 <= bounding[2] &&
          bounding[1] <= y0 && y1 <= bounding[3]) {
        overlapped = true;
      }

      x0 = Math.max(x0, bounding[0]);
      y0 = Math.max(y0, bounding[1]);
      x1 = Math.min(x1, bounding[2]);
      y1 = Math.min(y1, bounding[3]);

      for (let i = y0; i <= y1; i++) {
        let offset = i * width;
        for (let j = x0; j <= x1; j++) {
          if (maskPixels[offset + j]) {
            intersected = true;
          } else {
            if (intersected) {
              return INTERSECTION;
            }
            emptyPixels++;
          }
        }
      }

      if (overlapped && emptyPixels == 0 && intersected) {
        return INSIDE;
      }

      if (intersected) {
        return INTERSECTION;
      }

      return MISS;

    };

  })(),

  markVerticesInsideMask : (function() {

    const vertex = vec3.create();
    const pixel = vec2.create();

    return function(maskPixels, depthPixels, width, height, indices, offset, length,
        positions, vertsMask, mvp, near, far, linearDepth) {

      const startInd = offset;
      const finalInd = startInd + length;
      let numSelected = 0;

      for (let i = startInd; i < finalInd; ++i) {

        const ind = indices[i];

        if (vertsMask[ind]) {
          continue;
        }

        const offset = ind * 3;

        vec3.set(vertex, positions[offset], positions[offset + 1], positions[offset + 2]);
        vec3.transformMat4(vertex, vertex, mvp);

        const pureZCPU = 0.5 * (vertex[2] + 1);

        if (0.0 < pureZCPU && pureZCPU < 1.0) {

          NDCtoScreen(vertex[0], vertex[1], width, height, pixel);

          if (inScreen(pixel[0], pixel[1], width, height)) {

            const screenX = Math.round(pixel[0]);
            const screenY = Math.round(pixel[1]);

            const colorOffset = screenY * width + screenX;

            if (!maskPixels[colorOffset]) {
              continue;
            }

            if (depthPixels) {

              const depthOffset = 4 * colorOffset;
              const depth = getDepth(depthOffset, depthPixels);

              let linearZGPU, linearZCPU;
              if (linearDepth) {
                linearZGPU = orthoDepthToEye(depth, near, far);
                linearZCPU = orthoDepthToEye(pureZCPU, near, far);
              } else {
                linearZGPU = perspDepthToEye(depth, near, far);
                linearZCPU = perspDepthToEye(pureZCPU, near, far);
              }

              const delta = Math.min(0.01 * linearZCPU, 1.0);

              if (linearZCPU > linearZGPU + delta) {
                continue;
              }

            }

            if (maskPixels[colorOffset]) {
              vertsMask[ind] = 1;
              ++numSelected;
            }

          }

        }

      }

      return numSelected;

    };

  })(),

  AABBRayIntersect(from, to, bb) {

    const fromX = from[0];
    const fromY = from[1];
    const fromZ = from[2];

    const dirX = 1.0 / to[0];
    const dirY = 1.0 / to[1];
    const dirZ = 1.0 / to[2];

    const t1 = (bb[0] - fromX) * dirX;
    const t3 = (bb[1] - fromY) * dirY;
    const t5 = (bb[2] - fromZ) * dirZ;
    const t2 = (bb[3] - fromX) * dirX;
    const t4 = (bb[4] - fromY) * dirY;
    const t6 = (bb[5] - fromZ) * dirZ;

    const tmin = Math.max(Math.min(t1, t2), Math.min(t3, t4), Math.min(t5, t6));
    const tmax = Math.min(Math.max(t1, t2), Math.max(t3, t4), Math.max(t5, t6));

    return tmax >= 0 && tmin <= tmax;

  },

  AABBRayDistance(from, to, bb) {

    const fromX = from[0];
    const fromY = from[1];
    const fromZ = from[2];

    const dirX = 1.0 / to[0];
    const dirY = 1.0 / to[1];
    const dirZ = 1.0 / to[2];

    const t1 = (bb[0] - fromX) * dirX;
    const t3 = (bb[1] - fromY) * dirY;
    const t5 = (bb[2] - fromZ) * dirZ;
    const t2 = (bb[3] - fromX) * dirX;
    const t4 = (bb[4] - fromY) * dirY;
    const t6 = (bb[5] - fromZ) * dirZ;

    const tmin = Math.max(Math.min(t1, t2), Math.min(t3, t4), Math.min(t5, t6));
    const tmax = Math.min(Math.max(t1, t2), Math.max(t3, t4), Math.max(t5, t6));

    const sign = tmax >= 0 && tmin <= tmax ? 1 : -1;

    return sign * tmin;

  },

  pointInsideAABB(point, bb) {
    return point[0] > bb[0] && point[0] < bb[3] && point[1] > bb[1] && point[1] < bb[4] &&
        point[2] > bb[2] && point[2] < bb[5];
  },

  AABBPointDistance(point, bb) {
    const dx = Math.max(bb[0] - point[0], 0, point[0] - bb[3]);
    const dy = Math.max(bb[1] - point[1], 0, point[1] - bb[4]);
    const dz = Math.max(bb[2] - point[2], 0, point[2] - bb[5]);
    return Math.sqrt(dx*dx + dy*dy + dz*dz);
  },

  planeAABBTest : (function() {

    const vec3Tmp = vec3.create();
    const vec3Tmp2 = vec3.create();

    return function(plane, bb) {
      const center = vec3.set(vec3Tmp, 0.5 * (bb[0] + bb[3]),
                                       0.5 * (bb[1] + bb[4]),
                                       0.5 * (bb[2] + bb[5]));
      const extents = vec3.set(vec3Tmp2, bb[3] - center[0], bb[4] - center[1], bb[5] - center[2]);
      const s = vec3.dot(plane, center) + plane[3];
      const r = extents[0] * Math.abs(plane[0]) + extents[1] * Math.abs(plane[1]) + extents[2] * Math.abs(plane[2]);

      if (Math.abs(s) <= r) {
        return INTERSECTION;
      } else {
        return s < 0 ? MISS : INSIDE;
      }
    }
  })(),

  markVerticesBehindPlane : function (plane, indices, offset,
                                       length, positions, vertsMask) {

    let numSelected = 0;

    const startInd = offset;
    const finalInd = startInd + length;

    for (let i = startInd; i < finalInd; ++i) {

      const ind = indices[i];

      if (vertsMask[ind]) {
        continue;
      }

      const offset = ind * 3;

      const x = positions[offset];
      const y = positions[offset + 1];
      const z = positions[offset + 2];

      if (plane[0] * x + plane[1] * y + plane[2] * z + plane[3] > 0) {

        vertsMask[ind] = 1;
        ++numSelected;

      }

    }

    return numSelected;

  },

  planeSphereTest(plane, center, radius) {
    const dist = Math.abs(vec3.dot(plane, center) + plane[3]);
    return radius >= dist;
  },

  AABBAABBTest(bb1, bb2) {

    if (bb1[3] < bb2[0] || bb1[0] > bb2[3] ||
        bb1[4] < bb2[1] || bb1[1] > bb2[4] ||
        bb1[5] < bb2[2] || bb1[2] > bb2[5]) {
      return false;
    } else {
      return true;
    }

  },

  raySphereIntersect : (function() {

    const vec3Tmp = vec3.create();

    return function(origin, ray, center, radius, dest) {

      const toOrigin = vec3.sub(vec3Tmp, origin, center);
      const a = vec3.dot(ray, ray);
      const k = vec3.dot(ray, toOrigin);
      const c = vec3.dot(toOrigin, toOrigin) - radius * radius;

      const d = k * k - a * c;

      if (d == 0 || a == 0) {
        return false;
      }

      dest[0] = (-k - Math.sqrt(d)) / a;
      dest[1] = (-k + Math.sqrt(d)) / a;

      return true;

    }

  })(),

  AABBSphereIntersect(center, squaredRadius, bb, scale) {

    const cx = center[0];
    const cy = center[1];
    const cz = center[2];

    const lowx = bb[0] - cx;
    const lowy = bb[1] - cy;
    const lowz = bb[2] - cz;

    const upx = cx - bb[3];
    const upy = cy - bb[4];
    const upz = cz - bb[5];

    const outx = Math.max(lowx, upx, 0) * scale[0];
    const outy = Math.max(lowy, upy, 0) * scale[1];
    const outz = Math.max(lowz, upz, 0) * scale[2];

    if ((outx * outx + outy * outy + outz * outz) > squaredRadius) {
      return MISS;
    }

    const inx = Math.max(-Math.min(lowx, upx), 0) * scale[0];
    const iny = Math.max(-Math.min(lowy, upy), 0) * scale[1];
    const inz = Math.max(-Math.min(lowz, upz), 0) * scale[2];

    if ((inx * inx + iny * iny + inz * inz) < squaredRadius) {
      return INSIDE;
    }

    return INTERSECTION;

  },

  linePlaneTest : (function() {

    const vec3Tmp = vec3.create();

    return function(planeNormal, planePoint, from, to, dest) {

      const dotNT = vec3.dot(planeNormal, to);

      if (Math.abs(dotNT) < EPSILON) {
        return false;
      }

      const w = vec3.sub(vec3Tmp, from, planePoint);
      const t = - vec3.dot(planeNormal, w) / dotNT;

      vec3.scaleAndAdd(dest, w, to, t);
      vec3.add(dest, dest, planePoint);

      return true;

    }

  })(),

  rayPlaneIntersection : (function() {

    return function(plane, from, to, dest) {

      const dotPV = vec3.dot(plane, to);

      if (Math.abs(dotPV) < EPSILON) {
        return -1;
      }

      const t = -(plane[3] + vec3.dot(plane, from)) / dotPV;

      vec3.scaleAndAdd(dest, from, to, t);

      return t;

    }

  })(),

  calcThreePlanesIntersection : (function() {

    const mat3Tmp = mat3.create();

    return function(plane1, plane2, plane3, out) {

      const matrix = mat3.set(mat3Tmp, plane1[0], plane2[0], plane3[0],
                        plane1[1], plane2[1], plane3[1],
                        plane1[2], plane2[2], plane3[2]);
      const det = mat3.determinant(matrix);

      if (det == 0) {
        return null;
      }

      const matrixx = mat3.set(mat3Tmp, -plane1[3], -plane2[3], -plane3[3],
                        plane1[1], plane2[1], plane3[1],
                        plane1[2], plane2[2], plane3[2]);
      out[0] = mat3.determinant(matrixx) / det;

      const matrixy = mat3.set(mat3Tmp, plane1[0], plane2[0], plane3[0],
                        -plane1[3], -plane2[3], -plane3[3],
                        plane1[2], plane2[2], plane3[2]);
      out[1] = mat3.determinant(matrixy) / det;

      const matrixz = mat3.set(mat3Tmp, plane1[0], plane2[0], plane3[0],
                        plane1[1], plane2[1], plane3[1],
                        -plane1[3], -plane2[3], -plane3[3]);
      out[2] = mat3.determinant(matrixz) / det;

      return out;

    }

  })(),

  perspDepthToEye,
  orthoDepthToEye

};

const scaleFactor = 1.0 / 255.0;

function getDepth(offset, pixels) {

  const r = pixels[offset] * scaleFactor;
  const g = pixels[offset + 1] * scaleFactor;
  const b = pixels[offset + 2] * scaleFactor;

  return unpackDepth(r, g, b);

}

function unpackDepth(r, g, b) {
  return r * depthScale[0] + g * depthScale[1] + b * depthScale[2];
}

function NDCtoScreen(x, y, width, height, dest) {
  return vec2.set(dest, 0.5 * width * (x + 1.0), height - 0.5 * height * (1.0 - y));
}

function inScreen(x, y, width, height) {
  return x >= 0 && x < width && y >= 0 && y < height;
}

function readValue(mask, x, y, width) {
  return mask[y * width + x];
}

function clamp(min, max, value) {
  return Math.max(min, Math.min(value, max));
}

function perspDepthToEye(depth, near, far) {
  return 2.0 * near * far / (far + near - (2.0 * depth - 1.0) * (far - near));
}

function orthoDepthToEye(depth, near, far) {
  return depth * (far - near) + near;
}

export default intersectEvaluator;
